import React, { useEffect, useMemo, useState } from "react";
import {
    IonAccordion,
    IonAccordionGroup,
    IonBadge,
    IonButton,
    IonCol,
    IonGrid,
    IonIcon,
    IonItem,
    IonLabel,
    IonRow,
    IonSearchbar,
    IonText,
    IonToggle,
} from "@ionic/react";
import type { ToggleCustomEvent } from "@ionic/core/components";
import { addOutline, addSharp } from "ionicons/icons";
import type { Mapping, Provider } from "../AcaSettingsTypes";

interface AcaSettingsProvidersSectionProps {
    data: {
        providers: Provider[];
        mappings: Mapping[];
    };
    actions: {
        openAdd: () => void;
        toggleProvider: (provider: Provider, ev?: ToggleCustomEvent) => void;
    };
}

const AcaSettingsProvidersSection: React.FC<AcaSettingsProvidersSectionProps> = ({data, actions,}) => {
    const { providers, mappings } = data;
    const { openAdd, toggleProvider } = actions;

    const [providerQuery, setProviderQuery] = useState("");
    const [provExpanded, setProvExpanded] = useState<string[]>([]);

    const statesByProviderId = useMemo(() => {
        const map: Record<number, string[]> = {};
        (mappings || []).forEach((mapping) => {
            if (!map[mapping.providerId]) {
                map[mapping.providerId] = [];
            }
            if (!map[mapping.providerId].includes(mapping.state)) {
                map[mapping.providerId].push(mapping.state);
            }
        });
        return map;
    }, [mappings]);

    const activeProviders = useMemo(
        () =>
            (providers || [])
                .filter((provider) => provider.status === "ACTIVE")
                .filter((provider) => provider.name.toLowerCase().includes(providerQuery.toLowerCase()))
                .sort((a, b) => a.name.localeCompare(b.name)),
        [providers, providerQuery]
    );

    const inactiveProviders = useMemo(
        () =>
            (providers || [])
                .filter((provider) => provider.status === "INACTIVE")
                .filter((provider) => provider.name.toLowerCase().includes(providerQuery.toLowerCase()))
                .sort((a, b) => a.name.localeCompare(b.name)),
        [providers, providerQuery]
    );

    useEffect(() => {
        if (!providerQuery.trim()) {
            setProvExpanded([]);
            return;
        }
        const open: string[] = [];
        if (activeProviders.length) {
            open.push("prov-active");
        }
        if (inactiveProviders.length) {
            open.push("prov-inactive");
        }
        setProvExpanded(open);
    }, [providerQuery]);

    const renderProviderTiles = (list: Provider[]) => (
        <IonGrid>
            <IonRow>
                {list.map((provider) => {
                    const count = (statesByProviderId[provider.id] || []).length;
                    return (
                        <IonCol size="12" sizeSm="6" sizeMd="4" sizeLg="3" key={provider.id}>
                            <IonItem lines="full">
                                <IonCol size="9">
                                    <IonLabel className="ion-text-wrap">
                                        <IonText>{provider.name}</IonText>
                                    </IonLabel>
                                </IonCol>
                                {provider.status === "ACTIVE" && (
                                    <IonCol size="1">
                                        <IonBadge title="States mapped">{count}</IonBadge>
                                    </IonCol>
                                )}
                                <IonCol size="1">
                                    <IonToggle
                                        checked={provider.status === "ACTIVE"}
                                        onIonChange={(ev) => toggleProvider(provider, ev)}
                                    />
                                </IonCol>
                            </IonItem>
                        </IonCol>
                    );
                })}
            </IonRow>
        </IonGrid>
    );

    return (
        <IonAccordion value="sec-providers">
            <IonItem slot="header" color="light" lines="full">
                <IonLabel>Providers</IonLabel>
            </IonItem>
            <div slot="content">
                <section className="panel panel-default">
                    <IonGrid>
                        <IonRow className="ion-align-items-end">
                            <IonCol size="12" sizeMd="4">
                                <IonSearchbar
                                    value={providerQuery}
                                    placeholder="Search providers"
                                    onIonInput={(e) =>
                                        setProviderQuery((e.detail.value ?? "").toString())
                                    }
                                    onIonClear={() => setProviderQuery("")}
                                />
                            </IonCol>
                            <IonCol size="12" sizeMd="2" className="btn-col ion-align-self-end">
                                <IonButton onClick={openAdd}>
                                    <IonIcon slot="icon-only" md={addOutline} ios={addSharp} />
                                </IonButton>
                            </IonCol>
                        </IonRow>
                    </IonGrid>

                    <IonAccordionGroup
                        multiple
                        style={{ display: "grid", gap: 6 }}
                        value={provExpanded}
                        onIonChange={(e) =>
                            setProvExpanded(Array.isArray(e.detail.value) ? e.detail.value : [])
                        }
                    >
                        <IonAccordion value="prov-active">
                            <IonItem slot="header" color="light" lines="full">
                                <IonLabel>Active Providers</IonLabel>
                                <IonBadge slot="end">{activeProviders.length}</IonBadge>
                            </IonItem>
                            <div slot="content">{renderProviderTiles(activeProviders)}</div>
                        </IonAccordion>

                        <IonAccordion value="prov-inactive">
                            <IonItem slot="header" color="light" lines="full">
                                <IonLabel>Inactive Providers</IonLabel>
                                <IonBadge slot="end">{inactiveProviders.length}</IonBadge>
                            </IonItem>
                            <div slot="content">{renderProviderTiles(inactiveProviders)}</div>
                        </IonAccordion>
                    </IonAccordionGroup>
                </section>
            </div>
        </IonAccordion>
    );
};

export default AcaSettingsProvidersSection;